import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { Base_URL } from "../../url";
import { getCompanyByOrganisationId } from "../../Api/IncomingPo";

import "../../Styling/generalLedger.css";
import { FaFilter } from "react-icons/fa";

const CommonLedger = () => {
  const { user, token } = useAuth();

  const [ledgerRows, setLedgerRows] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  const [companyList, setCompanyList] = useState([]);
  const [codeList, setCodeList] = useState([]);

  const [orgId] = useState(user?.organisationId || "");
  const [companyCode, setCompanyCode] = useState("");
  const [ledgerType, setLedgerType] = useState("");
  const [code, setCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgId) return;

    getCompanyByOrganisationId(orgId)
      .then((res) => setCompanyList(res || []))
      .catch(console.error);
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !ledgerType) {
      setCodeList([]);
      return;
    }

    let url = "";
    let params = {};

    switch (ledgerType) {
      case "VENDOR":
        url = `${Base_URL}/VendorMaster`;
        params = { id: orgId };
        break;

      case "GL":
        url = `${Base_URL}/AllLeger/getAllGlName`;
        params = { orgId };
        break;

      case "ITEM":
        url = `${Base_URL}/AllLeger/items-by-org`;
        params = { orgId };
        break;

      case "ASSET":
        url = `${Base_URL}/AllLeger/assets-by-org`;
        params = { orgId };
        break;

      case "RESOURCE":
        url = `${Base_URL}/AllLeger/GetResourcesByOrg`;
        params = { orgId };
        break;

      case "CUSTOMER":
        url = `${Base_URL}/CustomerMaster/CustomerGetData`;
        params = { id: orgId };
        break;

      case "COSTCENTRE":
        url = `${Base_URL}/CostCenterMaster/GetAllCostCenters`;
        break;

      
      case "PROFITCENTER":
        url = `${Base_URL}/ProfitCenter/GetByOrgId`;
        params = { orgId };
        break;

      
      case "BUSINESSUNIT":
        url = `${Base_URL}/BusinessUnit/GetByOrgId`;
        params = { orgId };
        break;

      
      case "CONTACT":
        url = `${Base_URL}/Contact/GetByOrgId`;
        params = { orgId };
        break;

      case "BANK":
        url = `${Base_URL}/BankMaster/GetAllBanks/id?`;
        params = { id: orgId };
        break;

      default:
        return;
    }

    axios
      .get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        console.log("Code List:", res.data);
        setCodeList(res.data || []);
      })
      .catch((err) => console.error(err));

    setCode("");
  }, [ledgerType, orgId, token]);

  const fetchLedger = async () => {
    if (!ledgerType) {
      alert("Please select Ledger Type");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(
        `${Base_URL}/common-ledger`,
        {
          orgId,
          companyCode,
          ledgerType,
          code,
          fromDate: fromDate || null,
          toDate: toDate || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setLedgerRows(res.data.ledgerRows || []);
      setOpeningBalance(res.data.openingBalance || 0);
      setClosingBalance(res.data.closingBalance || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  
  const tableRows = useMemo(() => {
    if (!ledgerRows.length) return [];

    return [
      {
        type: "OPENING",
        date: fromDate || ledgerRows[0]?.date,
        closingBalance: openingBalance
      },
      ...ledgerRows.map((r) => ({ ...r, type: "TX" })),
      {
        type: "CLOSING",
        date: toDate || ledgerRows[ledgerRows.length - 1]?.date,
        closingBalance: closingBalance
      }
    ];
  }, [ledgerRows, openingBalance, closingBalance, fromDate, toDate]);


  const getOptionValue = (c) => {
    switch (ledgerType) {
      case "VENDOR": return c.vendorCode;
      case "GL": return c.glCode;
      case "ITEM": return c.itemCode;
      case "ASSET": return c.assetId;
      case "RESOURCE": return c.resourceId;
      case "CUSTOMER": return c.customerCode;
      case "COSTCENTRE": return c.costCenterCode;

      case "PROFITCENTER": return c.profitcenterCode;

      case "BUSINESSUNIT": return c.businessunitCode;

      case "BANK": return c.code;

      case "CONTACT": return c.contactId;

      default: return "";
    }
  };

  const getOptionLabel = (c) => {
    switch (ledgerType) {
      case "VENDOR": return `${c.name} (${c.vendorCode})`;
      case "GL": return `${c.glName} (${c.glCode})`;
      case "ITEM": return `${c.itemName} (${c.itemCode})`;
      case "ASSET": return `${c.assetName} (${c.assetId})`;
      case "RESOURCE": return `${c.resourceName} (${c.resourceId})`;
      case "CUSTOMER": return `${c.name} (${c.customerCode})`;
      case "COSTCENTRE": return `${c.costCenterName} (${c.costCenterCode})`;

      case "PROFITCENTER":
        return `${c.profitcenterName} (${c.profitcenterCode})`;

      case "BUSINESSUNIT":
        return `${c.businessunitName} (${c.businessunitCode})`;

      case "BANK": return `${c.description} (${c.code})`;

      case "CONTACT":
        return `${c.contactName} (${c.contactId})`;

      default: return "";
    }
  };

  return (
    <div className="general-ledger-container">
      <h2>Common Ledger</h2>

      <div className="filters-section">
        <div className="filters-header">
          <FaFilter /> Filters
        </div>

        <div className="ledger-filters">

          <input type="text" value={orgId} disabled className="filter-input" />

          <select
            value={companyCode}
            onChange={(e) => setCompanyCode(e.target.value)}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companyList.map((c, i) => (
              <option key={i} value={c.companyCode}>
                {c.name} - {c.companyCode}
              </option>
            ))}
          </select>

          <select
            value={ledgerType}
            onChange={(e) => setLedgerType(e.target.value)}
            className="filter-select"
          >
            <option value="">Select Ledger Type</option>
            <option value="VENDOR">Vendor</option>
            <option value="GL">General Ledger</option>
            <option value="ITEM">Item</option>
            <option value="ASSET">Asset</option>
            <option value="RESOURCE">Resource</option>
            <option value="CUSTOMER">Customer</option>
            <option value="COSTCENTRE">Cost Centre</option>
            <option value="PROFITCENTER">Profit Center</option>
            <option value="BANK">Bank</option>
            <option value="BUSINESSUNIT">Business Unit</option>
            <option value="CONTACT">Contact</option>
          </select>

          <select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="filter-select"
          >
            <option value="">Select Code</option>
            {codeList.map((c, i) => (
              <option key={i} value={getOptionValue(c)}>
                {getOptionLabel(c)}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="filter-input"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="filter-input"
          />

          <button className="btn btn-primary" onClick={fetchLedger}>
            APPLY FILTER
          </button>
        </div>
      </div>

      <table className="ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Doc No</th>
            <th>Ref</th>
            <th>Description</th>
            <th>Debit</th>
            <th>Credit</th>
            <th>Balance</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr><td colSpan="7">Loading...</td></tr>
          ) : tableRows.length === 0 ? (
            <tr><td colSpan="7">No data</td></tr>
          ) : (
            tableRows.map((r, i) => (
              <tr key={i}>
                <td>{r.date?.split("T")[0]}</td>
                <td>{r.type === "TX" ? r.documentNo : ""}</td>
                <td>{r.type === "TX" ? r.refNo : ""}</td>
                <td>
                  {r.type === "OPENING"
                    ? "Opening Balance"
                    : r.type === "CLOSING"
                    ? "Closing Balance"
                    : r.name}
                </td>
                <td>{r.debit || ""}</td>
                <td>{r.credit || ""}</td>
                <td>{Number(r.closingBalance || 0).toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonLedger;